import type { MouseEvent as ReactMouseEvent } from "react";

/**
 * `useTriggerToggle`가 다루는 최소 팝업 인터페이스. `useAnchoredPopupAnchor()`가 구조적으로 만족한다
 * (foundations가 AnchoredPopup 컴포넌트에 의존하지 않도록 최소 계약만 둔다).
 */
export interface ToggleablePopupApi {
  open: boolean;
  openFromElement: (event: ReactMouseEvent<HTMLElement>) => void;
  close: () => void;
}

/**
 * AnchoredPopup 트리거의 open/close/**재클릭 토글** 공용 훅. click에서 open/close를 토글한다 —
 * `openFromElement`가 트리거를 anchor `el`로 잡고 AnchoredPopup이 그 el을 outside-dismiss 경계 안으로
 * 보므로 트리거 pointerdown이 먼저 닫는 레이스가 없다. `useAnchoredPopupAnchor`를 쓰는 어떤
 * 컴포넌트든 트리거에 `triggerProps`를 spread하면 된다(값·도메인 무관 — picker 전용 아님).
 */
export function useTriggerToggle(popup: ToggleablePopupApi, disabled: boolean) {
  const open = (event: ReactMouseEvent<HTMLElement>) => {
    if (disabled) return;
    popup.openFromElement(event);
  };

  const triggerProps = {
    onClick: (event: ReactMouseEvent<HTMLElement>) => {
      if (disabled) return;
      if (popup.open) popup.close();
      else popup.openFromElement(event);
    },
    "aria-haspopup": "dialog" as const,
    "aria-expanded": popup.open,
  };

  return { triggerProps, open, close: popup.close };
}
